import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Users, Truck, Store, Heart, Target, Award } from 'lucide-react';
import SEO from '../components/SEO';

export default function AboutPage() {
  const { t } = useTranslation();

  const stats = [
    { value: '500+', labelKey: 'about.restaurantPartners' },
    { value: '50,000+', labelKey: 'about.happyCustomers' },
    { value: '100,000+', labelKey: 'about.ordersDelivered' },
    { value: '30 min', labelKey: 'about.avgDelivery' },
  ];

  const values = [
    { icon: Heart, titleKey: 'about.customerFirst', descKey: 'about.customerFirstDesc' },
    { icon: Target, titleKey: 'about.qualitySpeed', descKey: 'about.qualitySpeedDesc' },
    { icon: Award, titleKey: 'about.trustSafety', descKey: 'about.trustSafetyDesc' },
  ];

  return (
    <>
      <SEO page="about" />

      <div className="min-h-screen bg-slate-50 pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-orange-500 to-orange-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{t('about.title')}</h1>
            <p className="text-xl text-orange-100 leading-relaxed">
              {t('about.heroSubtitle')}
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.labelKey} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-orange-500 mb-2">{stat.value}</div>
                  <div className="text-slate-600">{t(stat.labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">{t('about.ourStory')}</h2>
            <div className="prose prose-lg max-w-none text-slate-600">
              <p className="mb-6">
                {t('about.storyP1')}
              </p>
              <p className="mb-6">
                {t('about.storyP2')}
              </p>
              <p>
                {t('about.storyP3')}
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">{t('about.ourValues')}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value) => {
                const Icon = value.icon;
                return (
                  <div key={value.titleKey} className="text-center p-6 rounded-2xl bg-slate-50">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                      <Icon className="w-8 h-8 text-orange-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{t(value.titleKey)}</h3>
                    <p className="text-slate-600">{t(value.descKey)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">{t('about.joinTeam')}</h2>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
              {t('about.joinTeamDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/apply/driver" 
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 transition-colors"
              >
                <Truck className="w-5 h-5" />
                {t('about.becomeDriver')}
              </Link>
              <Link 
                to="/register?role=restaurant_admin" 
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-orange-500 text-orange-500 rounded-full font-medium hover:bg-orange-50 transition-colors"
              >
                <Store className="w-5 h-5" />
                {t('about.partnerRestaurant')}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
